/**
 * The board `···` ▸ **View archive / trash** dialog (`specs/M1-B10-shell-chrome.md` §9, q452).
 *
 * monday's entry is a plain row, not a submenu (`[c:board-menu]` — hovering it opens nothing),
 * and it is **board scoped**: this board's archived and deleted *items*, not the account-level
 * Archive and Trash screens the avatar menu already has. 6f left it disabled because the
 * listing did not exist; `items.listRemoved` is it.
 *
 * One dialog with monday's two destinations as tabs, because they are the same table with a
 * different timestamp — the shape `RemovedBoardsScreen` already uses for the account-level
 * pair, so the two screens read alike.
 *
 * `Restore` calls `items.restore`, which handles both states and relocates a row whose group
 * is gone (B3 D3); the row says so in advance through `groupGone`, so the move is never a
 * surprise.
 */
import { useState } from 'react';
import { Icon, formatMondayDateTime, icons, useOverlayDismiss, useToast } from '@elizmonday/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPCClient } from '../../lib/trpc';
import './boards.css';

export type RemovedItemsTab = 'archived' | 'deleted';

interface RemovedItem {
  id: string;
  name: string;
  groupId: string;
  groupTitle: string | null;
  groupGone: boolean;
  byName: string | null;
  at: string | null;
  canRestore: boolean;
}

const TABS: readonly { id: RemovedItemsTab; label: string; empty: string; when: string }[] = [
  { id: 'archived', label: 'Archive', empty: 'Nothing archived on this board.', when: 'Archived' },
  { id: 'deleted', label: 'Trash', empty: 'Nothing in this board’s trash.', when: 'Deleted' },
];

/** The same absolute stamp the account-level screens show — never a relative "3 days ago". */
function formatWhen(at: string | null): string {
  if (!at) return '—';
  const date = new Date(at);
  if (Number.isNaN(date.getTime())) return '—';
  return formatMondayDateTime(date, { hour: '2-digit', minute: '2-digit' });
}

export function BoardArchiveDialog({
  boardId,
  boardName,
  onClose,
}: {
  boardId: string;
  boardName: string;
  onClose(): void;
}) {
  const client = useTRPCClient();
  const queryClient = useQueryClient();
  const toast = useToast();
  /** P-260: Escape closes it, and focus returns to the menu row that opened it. */
  useOverlayDismiss({ onClose });

  const [tab, setTab] = useState<RemovedItemsTab>('archived');
  const copy = TABS.find((entry) => entry.id === tab) ?? TABS[0]!;

  const rows = useQuery({
    queryKey: ['items', 'removed', boardId, tab],
    queryFn: () => client.items.listRemoved.query({ boardId, state: tab }) as Promise<RemovedItem[]>,
  });

  const restore = async (row: RemovedItem) => {
    await client.items.restore.mutate({ itemId: row.id });
    await queryClient.invalidateQueries({ queryKey: ['items', 'removed', boardId] });
    await queryClient.invalidateQueries({ queryKey: ['board'] });
    toast(
      row.groupGone
        ? `${row.name} restored to the first group — its own group is gone`
        : `${row.name} restored`,
      { kind: 'positive' },
    );
  };

  const list = rows.data ?? [];

  return (
    <div className="ez-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="ez-board-dialog ez-board-dialog--wide"
        role="dialog"
        aria-modal="true"
        aria-label={`Archive and trash for ${boardName}`}
        data-testid="board-archive-dialog"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 className="ez-board-dialog__title">Archive / trash</h2>
        <p className="ez-board-dialog__hint">{`The items removed from ${boardName}.`}</p>

        <div className="ez-board-dialog__tabs" role="tablist" aria-label="Archive or trash">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={tab === entry.id}
              data-testid={`board-archive-tab-${entry.id}`}
              onClick={() => setTab(entry.id)}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {rows.isPending ? (
          <p className="ez-removed__empty">Loading…</p>
        ) : list.length === 0 ? (
          <p className="ez-removed__empty" data-testid="board-archive-empty">
            {copy.empty}
          </p>
        ) : (
          <table className="ez-removed__table">
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col">Group</th>
                <th scope="col">By</th>
                <th scope="col">{copy.when}</th>
                <th scope="col">
                  <span className="ez-visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id} data-testid={`board-archive-row-${row.id}`}>
                  <td>
                    <span className="ez-removed__name">
                      <Icon icon={icons.item} size={16} ignoreFocusStyle />
                      {row.name}
                    </span>
                  </td>
                  <td>
                    {row.groupTitle ?? '—'}
                    {row.groupGone ? <span className="ez-removed__no-action"> (gone)</span> : null}
                  </td>
                  <td>{row.byName ?? '—'}</td>
                  <td>{formatWhen(row.at)}</td>
                  <td className="ez-removed__actions">
                    {row.canRestore ? (
                      <button
                        type="button"
                        className="ez-removed__action"
                        data-testid={`board-archive-restore-${row.id}`}
                        onClick={() => void restore(row)}
                      >
                        Restore
                      </button>
                    ) : (
                      <span className="ez-removed__no-action">No access</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <footer className="ez-board-dialog__footer">
          <button type="button" className="ez-board-dialog__cancel" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
